import axios from "axios";

interface BackendErrorMessageOptions {
  includeFieldPath?: boolean;
}

interface ExtractedError {
  message: string;
  path: Array<string | number>;
}

function normalizeErrorString(value: string, allowErrorDetail: boolean): string | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === "none") {
    return null;
  }

  if (trimmed.includes("ErrorDetail(")) {
    if (!allowErrorDetail) {
      return null;
    }

    const match = trimmed.match(/ErrorDetail\(string=['"](.+?)['"],\s*code=/);
    return match?.[1]?.trim() || trimmed;
  }

  if (!trimmed.includes(" ") && trimmed.includes("_") && trimmed === trimmed.toUpperCase()) {
    return null;
  }

  return trimmed;
}

function extractFirstErrorString(value: unknown, allowErrorDetail = true): string | null {
  if (typeof value === "string") {
    return normalizeErrorString(value, allowErrorDetail);
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      const extracted = extractFirstErrorString(entry, allowErrorDetail);
      if (extracted) {
        return extracted;
      }
    }
    return null;
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;

    const directMessage =
      typeof record.message === "string" ? normalizeErrorString(record.message, false) : null;
    if (directMessage) {
      return directMessage;
    }

    for (const key of ["detail", "details", "field_errors", "non_field_errors", "errors", "items", "data"]) {
      if (!(key in record)) {
        continue;
      }

      const extracted = extractFirstErrorString(record[key], true);
      if (extracted) {
        return extracted;
      }
    }

    const messageFallback =
      typeof record.message === "string" ? normalizeErrorString(record.message, true) : null;
    if (messageFallback) {
      return messageFallback;
    }

    const explicitError =
      typeof record.error === "string" ? normalizeErrorString(record.error, true) : null;
    if (explicitError) {
      return explicitError;
    }

    for (const [key, entry] of Object.entries(record)) {
      if (["message", "detail", "details", "field_errors", "non_field_errors", "errors", "items", "data", "error"].includes(key)) {
        continue;
      }

      const extracted = extractFirstErrorString(entry, true);
      if (extracted) {
        return extracted;
      }
    }
  }

  return null;
}

const ERROR_WRAPPER_KEYS = new Set([
  "detail",
  "details",
  "field_errors",
  "non_field_errors",
  "errors",
  "data",
]);

function extractFirstErrorWithPath(
  value: unknown,
  path: Array<string | number> = [],
  allowErrorDetail = true,
): ExtractedError | null {
  if (typeof value === "string") {
    const message = normalizeErrorString(value, allowErrorDetail);
    return message ? { message, path } : null;
  }

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      const entry = value[index];
      const entryPath = entry && typeof entry === "object" ? [...path, index] : path;
      const extracted = extractFirstErrorWithPath(entry, entryPath, allowErrorDetail);
      if (extracted) {
        return extracted;
      }
    }
    return null;
  }

  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const directMessage =
    typeof record.message === "string" ? normalizeErrorString(record.message, false) : null;
  if (directMessage) {
    return { message: directMessage, path };
  }

  for (const key of ["detail", "details", "field_errors", "non_field_errors", "errors", "items", "data"]) {
    if (!(key in record)) {
      continue;
    }
    const childPath = ERROR_WRAPPER_KEYS.has(key) ? path : [...path, key];
    const extracted = extractFirstErrorWithPath(record[key], childPath, true);
    if (extracted) {
      return extracted;
    }
  }

  const messageFallback =
    typeof record.message === "string" ? normalizeErrorString(record.message, true) : null;
  if (messageFallback) {
    return { message: messageFallback, path };
  }

  const explicitError =
    typeof record.error === "string" ? normalizeErrorString(record.error, true) : null;
  if (explicitError) {
    return { message: explicitError, path };
  }

  for (const [key, entry] of Object.entries(record)) {
    if (["message", "detail", "details", "field_errors", "non_field_errors", "errors", "items", "data", "error"].includes(key)) {
      continue;
    }
    const extracted = extractFirstErrorWithPath(entry, [...path, key], true);
    if (extracted) {
      return extracted;
    }
  }

  return null;
}

function humanizeFieldName(value: string): string {
  const label = value
    .replace(/_/g, " ")
    .replace(/\b(id|ids|usd|rfq|po|impa)\b/gi, (token) => token.toUpperCase());
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatErrorPath(path: Array<string | number>): string {
  const labels: string[] = [];
  for (let index = 0; index < path.length; index += 1) {
    const segment = path[index];
    const nextSegment = path[index + 1];
    if (segment === "items" && typeof nextSegment === "number") {
      labels.push(`Item ${nextSegment + 1}`);
      index += 1;
      continue;
    }
    if (typeof segment === "number") {
      labels.push(`Entry ${segment + 1}`);
      continue;
    }
    labels.push(humanizeFieldName(segment));
  }
  return labels.join(" — ");
}

export function extractBackendErrorMessage(
  error: unknown,
  fallback: string,
  options: BackendErrorMessageOptions = {},
): string {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }

  if (options.includeFieldPath) {
    const extracted = extractFirstErrorWithPath(error.response?.data);
    if (extracted) {
      const fieldPath = formatErrorPath(extracted.path);
      return fieldPath ? `${fieldPath}: ${extracted.message}` : extracted.message;
    }
    return fallback;
  }

  const extractedMessage = extractFirstErrorString(error.response?.data);
  if (extractedMessage) {
    return extractedMessage;
  }

  return fallback;
}
